import type { ComplianceSectionPdfLayout } from "./complianceSectionPdfLayout";
import type {
  MachineryDirectiveRiskAssessmentDocumentTranslations,
  RiskAssessmentSectionRole,
  SectionConfig,
} from "./types";

/** Base section entry: structure only. Text comes from translations JSON. */
interface BaseSectionEntry {
  section: string;
  parentSection?: string;
  /** Emit only the `##` heading during generation; body lives in child sections. */
  role?: "headerOnly";
  pdfLayout?: ComplianceSectionPdfLayout;
}

export const RISK_ASSESSMENT_HOUSEKEEPING_PARENT_SECTION_ID = "7.3";

export const RISK_ASSESSMENT_HOUSEKEEPING_CHILD_SECTION_IDS = [
  "7.3.1",
  "7.3.2",
  "7.3.3",
  "7.3.4",
] as const;

/**
 * Base section configurations for machinery directive risk assessment document generation.
 * User-facing strings come from locale JSON; LLM section behavior lives in server agents.
 */
const BASE_SECTION_CONFIGS: BaseSectionEntry[] = [
  { section: "1" },
  { section: "2" },
  { section: "2.1" },
  { section: "2.2" },
  { section: "2.3" },
  { section: "4.2" },
  { section: "4.3" },
  { section: "5.2" },
  { section: "5.3" },
  { section: "6.1" },
  { section: "6.2" },
  { section: "7.1" },
  { section: "7.2" },
  {
    section: RISK_ASSESSMENT_HOUSEKEEPING_PARENT_SECTION_ID,
    role: "headerOnly",
  },
  {
    section: RISK_ASSESSMENT_HOUSEKEEPING_CHILD_SECTION_IDS[0],
    parentSection: RISK_ASSESSMENT_HOUSEKEEPING_PARENT_SECTION_ID,
  },
  {
    section: RISK_ASSESSMENT_HOUSEKEEPING_CHILD_SECTION_IDS[1],
    parentSection: RISK_ASSESSMENT_HOUSEKEEPING_PARENT_SECTION_ID,
  },
  {
    section: RISK_ASSESSMENT_HOUSEKEEPING_CHILD_SECTION_IDS[2],
    parentSection: RISK_ASSESSMENT_HOUSEKEEPING_PARENT_SECTION_ID,
  },
  {
    section: RISK_ASSESSMENT_HOUSEKEEPING_CHILD_SECTION_IDS[3],
    parentSection: RISK_ASSESSMENT_HOUSEKEEPING_PARENT_SECTION_ID,
  },
  { section: "7.4" },
  { section: "8", pdfLayout: "landscape-page-start" },
];

function resolveBaseSectionRole(
  entry: BaseSectionEntry,
): RiskAssessmentSectionRole {
  if (entry.role === "headerOnly") {
    return "headerOnly";
  }
  if (entry.parentSection != null) {
    return "subsection";
  }
  return "content";
}

const RISK_ASSESSMENT_SECTION_ROLE_BY_ID = new Map<
  string,
  RiskAssessmentSectionRole
>(
  BASE_SECTION_CONFIGS.map((entry) => [
    entry.section,
    resolveBaseSectionRole(entry),
  ]),
);

/** Manifest role for a canonical risk assessment section id. */
export function getRiskAssessmentSectionRole(
  sectionId: string,
): RiskAssessmentSectionRole {
  return RISK_ASSESSMENT_SECTION_ROLE_BY_ID.get(sectionId) ?? "content";
}

export function isRiskAssessmentHeaderOnlySection(sectionId: string): boolean {
  return getRiskAssessmentSectionRole(sectionId) === "headerOnly";
}

export function isRiskAssessmentSubsectionSection(sectionId: string): boolean {
  return getRiskAssessmentSectionRole(sectionId) === "subsection";
}

const RISK_ASSESSMENT_PDF_LAYOUT_BY_ID = new Map<
  string,
  ComplianceSectionPdfLayout
>(
  BASE_SECTION_CONFIGS.flatMap((entry) =>
    entry.pdfLayout != null ? [[entry.section, entry.pdfLayout] as const] : [],
  ),
);

/** Manifest PDF layout for a canonical risk assessment section id. */
export function getRiskAssessmentSectionPdfLayout(
  sectionId: string,
): ComplianceSectionPdfLayout {
  return RISK_ASSESSMENT_PDF_LAYOUT_BY_ID.get(sectionId) ?? "portrait-continuous";
}

function isUndefinedOrEmpty(value: string | undefined | null): boolean {
  return value === undefined || value === null || value === "";
}

/**
 * Build full section configs by merging base structure with translations for the given locale.
 */
export function getSectionConfigs(
  translations: MachineryDirectiveRiskAssessmentDocumentTranslations,
): SectionConfig[] {
  const { sections: sectionsMap } = translations;
  return BASE_SECTION_CONFIGS.map((base) => {
    const sectionTranslations = sectionsMap[base.section];
    if (!sectionTranslations) {
      throw new Error(
        `Missing machinery directive risk assessment document translations for section "${base.section}". Ensure locale JSON has sections["${base.section}"].`,
      );
    }
    const config: SectionConfig = {
      section: base.section,
      role: resolveBaseSectionRole(base),
      title: sectionTranslations.title,
    };
    if (base.parentSection != null) {
      config.parentSection = base.parentSection;
    }
    if (!isUndefinedOrEmpty(sectionTranslations.hardcodedContent)) {
      config.hardcodedContent = sectionTranslations.hardcodedContent;
    }
    if (!isUndefinedOrEmpty(sectionTranslations.manualCompletionInstruction)) {
      config.manualCompletionInstruction =
        sectionTranslations.manualCompletionInstruction;
    }
    return config;
  });
}

/** Section ids in order (for filtering when sectionsToGenerate is provided). */
export const SECTION_IDS: string[] = BASE_SECTION_CONFIGS.map(
  (entry) => entry.section,
);

/**
 * When partial regen targets housekeeping parent `7.3`, include all child section ids.
 */
export function expandRiskAssessmentSectionsToGenerate(
  sectionsToGenerate: string[] | undefined,
): string[] | undefined {
  if (sectionsToGenerate == null) {
    return undefined;
  }

  const expanded = new Set(sectionsToGenerate);
  if (expanded.has(RISK_ASSESSMENT_HOUSEKEEPING_PARENT_SECTION_ID)) {
    for (const childSectionId of RISK_ASSESSMENT_HOUSEKEEPING_CHILD_SECTION_IDS) {
      expanded.add(childSectionId);
    }
  }

  return SECTION_IDS.filter((sectionId) => expanded.has(sectionId));
}
